/* eslint-disable max-len */
import Vue from 'vue';
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';

import {
  LookFullDTO, LookItemDTO, LookCategoryTranslation, ItemDTO, CatalogItemShortDTO,
} from '@/api';
import service from '@/services';
import { isRangeCrossed, throwError, getAssetUrl } from '@/utils';
import { Groups, GroupedLook } from '@/utils/looks.utils';

interface Look {
  id?: number;
  category: number;
  name?: string;
  createdAt?: number;
  lookPicture?: string;
  items?: Array<LookItemDTO>;
  temperatureFrom?: string;
  temperatureTo?: string;
}

export enum WeatherFilterEnum {
  ALL = 'all',
  WINTER = 'winter',
  SUMMER = 'summer',
  DEMI_SEASON_COLD = 'demi-season-cold',
  DEMI_SEASON_WARM = 'demi-season-warm',
  OTHER = 'other',
}

export const seasonsMap: Record<string, [number, number]> = {
  [WeatherFilterEnum.SUMMER]: [20, 60],
  [WeatherFilterEnum.DEMI_SEASON_WARM]: [10, 19],
  [WeatherFilterEnum.DEMI_SEASON_COLD]: [0, 9],
  [WeatherFilterEnum.WINTER]: [-60, -1],
};

export const weatherFilterList = {
  [WeatherFilterEnum.ALL]: { title: 'Все', min: -60, max: 60 },
  [WeatherFilterEnum.WINTER]: { title: 'Зима', min: seasonsMap[WeatherFilterEnum.WINTER][0], max: seasonsMap[WeatherFilterEnum.WINTER][1] },
  [WeatherFilterEnum.SUMMER]: { title: 'Лето', min: seasonsMap[WeatherFilterEnum.SUMMER][0], max: seasonsMap[WeatherFilterEnum.SUMMER][1] },
  [WeatherFilterEnum.DEMI_SEASON_COLD]: { title: 'Демисезонно холодный', min: seasonsMap[WeatherFilterEnum.DEMI_SEASON_COLD][0], max: seasonsMap[WeatherFilterEnum.DEMI_SEASON_COLD][1] },
  [WeatherFilterEnum.DEMI_SEASON_WARM]: { title: 'Демисезонно тёплый', min: seasonsMap[WeatherFilterEnum.DEMI_SEASON_WARM][0], max: seasonsMap[WeatherFilterEnum.DEMI_SEASON_WARM][1] },
};

export function getTempRange(thing: LookItemDTO): [number, number] {
  return [Number(thing.temperatureDTO?.min), Number(thing.temperatureDTO?.max)];
}

function fromCatalogThingToLookItem(item: CatalogItemShortDTO): LookItemDTO {
  return {
    name: item.name,
    catalogItemId: item.id,
    fileNames: [{ fileName: item.pictUrl && getAssetUrl(item.pictUrl) }],
    aiType: item.aiCategory,
  };
}

function fromThingToLookItem(item: ItemDTO): LookItemDTO {
  return {
    clientItemId: item.id,
    name: item.name,
    fileNames: item.fileNames,
    aiType: item.aiType,
  };
}

function grouping(items: LookItemDTO[]): Groups {
  const groups: Groups = {
    top2: [],
    bottom: [],
    top1: [],
    outerwear: [],
    dress: [],
    shoes: [],
    accessories: [],
    bag: [],
    jewelry: [],
  };
  items.forEach((item) => {
    const key = item.aiType as keyof typeof groups | undefined;
    if (!key) return;
    groups[key].push(item);
  });
  return groups;
}

const groupBy = <T, K extends keyof any>(list: T[], getKey: (item: T) => K) => list.reduce((previous, currentItem) => {
  const group = getKey(currentItem);
  // eslint-disable-next-line no-param-reassign
  if (!previous[group]) previous[group] = [];
  previous[group].push(currentItem);
  return previous;
}, {} as Record<K, T[]>);

function sortingByTempDiff(lookItems: LookItemDTO[], tempRange: [number, number]): LookItemDTO[] {
  const rangeDiff = tempRange[1] - tempRange[0];
  return lookItems.slice().sort((item1, item2) => {
    const item1diff = Number(item1.temperatureDTO?.max || 0)
        - Number(item1.temperatureDTO?.min || 0);
    const item2diff = Number(item2.temperatureDTO?.max || 0)
        - Number(item2.temperatureDTO?.min || 0);
    return (rangeDiff - item2diff) - (rangeDiff - item1diff);
  });
}

function filter(lookItems: LookItemDTO[], tempRange: [number, number]): LookItemDTO[] {
  const sorted = sortingByTempDiff(lookItems, tempRange);
  // оставляем все вещи которые попадают во входящий диапазон температуры
  const filteredItems = sorted.filter((item) => {
    const itemRange: [number, number] = [
      item.temperatureDTO?.min != null ? Number(item.temperatureDTO.min) : -50,
      item.temperatureDTO?.max != null ? Number(item.temperatureDTO.max) : 50,
    ];
    // проверяем попадает ли диапазон температуры вещи в диапазон входной
    return isRangeCrossed(itemRange, tempRange) && item.aiType;
  });

  const filteredItemsForCurrentTemperatureRange = groupBy(filteredItems, i => i.aiType as keyof Groups);

  if (isRangeCrossed(seasonsMap[WeatherFilterEnum.WINTER], tempRange) && !filteredItemsForCurrentTemperatureRange.outerwear?.length) {
    return [];
  }

  if (!filteredItemsForCurrentTemperatureRange.shoes?.length) {
    return []; // первая проверка на наличие обуви в которой можем выйти на улицу, если нет то пустой лук
  }

  // далее проверяем есть ли верхняя одежда на погоду или комплект верх-низ на погоду
  if (!filteredItemsForCurrentTemperatureRange.outerwear?.length // есть шуба - все ок, идем дальше. Если нет - проверяем платье
    && !filteredItemsForCurrentTemperatureRange.dress?.length // есть платье на погоду - все ок, идем дальше. Если нет - проверяем брюки-свитер
    && (!filteredItemsForCurrentTemperatureRange.bottom?.length // или есть штаны и топ
      || (!filteredItemsForCurrentTemperatureRange.top1?.length && !filteredItemsForCurrentTemperatureRange.top2?.length))
  ) {
    return [];
  }

  // мы здесь, значит можем выйти на улицу, далее строим и проверяем полный ли лук чтобы выйти на улицу и войти в офис
  // (т.е. если мы снимем шубу и смотрим будут ли под шубой платье или штаны с top1 или юбка c top1)
  // для этого мы берем самую холодную температуру из диапазона и смотрим от нее до, скажем, +100 градусов, чтобы получить все вещи
  const actualTemRange: [number, number] = [tempRange[0], 100];
  const finalItems = sorted.filter((item) => {
    const itemRange: [number, number] = [
      item.temperatureDTO?.min != null ? Number(item.temperatureDTO.min) : -50,
      item.temperatureDTO?.max != null ? Number(item.temperatureDTO.max) : 50,
    ];
    return isRangeCrossed(itemRange, actualTemRange);
  });
  const finalLookItems = groupBy(finalItems, i => i.aiType as keyof Groups);
  if (!finalLookItems.dress?.length
                && !(!!finalLookItems.bottom?.length && !!finalLookItems.top1?.length)) {
    return []; // нет комплекта показаться на люди
  }

  const finalGroup: Groups = {
    ...finalLookItems,
    outerwear: filteredItemsForCurrentTemperatureRange.outerwear,
    shoes: filteredItemsForCurrentTemperatureRange.shoes,
  };

  return Object.values(finalGroup).flat().filter(i => !!i); // прошли все проверки вернули отфильтрованный по погоде лук
}

@Module({ name: 'LooksModule', namespaced: true })
export default class ClientsModule extends VuexModule {
  loadingList = false;

  loadingThing = false;

  list: LookFullDTO[] = [];

  selected: number[] = [];

  multi = false;

  categories: LookCategoryTranslation[] = []

  weatherFilter: WeatherFilterEnum[] = [WeatherFilterEnum.ALL];

  tempFilter: [number, number] = [-50, 60];

  get selectedLooks() {
    return this.filteredLooks.filter(look => look.id && this.selected.includes(look.id));
  }

  get selectedLook() {
    return this.selectedLooks[this.selectedLooks.length - 1];
  }

  get filteredLooks() {
    if (this.weatherFilter.includes(WeatherFilterEnum.ALL)) {
      return this.groupedThingsByLook;
    }
    let filterRange: [number, number];
    if (this.weatherFilter.includes(WeatherFilterEnum.OTHER)) {
      filterRange = this.tempFilter;
    } else {
      const currentFilters = (this.weatherFilter as Exclude<WeatherFilterEnum, WeatherFilterEnum.OTHER>[]).map(i => weatherFilterList[i]);
      filterRange = currentFilters.reduce((acc, item) => {
        const min = item.min < acc[0] ? item.min : acc[0];
        const max = item.max > acc[1] ? item.max : acc[1];
        return [min, max];
      }, [60, -60]);
    }

    return this.groupedThingsByLook.reduce<GroupedLook[]>((arr, look) => {
      const filteredList = look.items ? filter(look.items, filterRange) : [];
      return filteredList.length ? arr.concat({ ...look, groups: grouping(filteredList) }) : arr;
    }, []);
  }

  get groupByCategory() {
    const categories = [
      {
        title: 'Удалённые пользователем',
        type: 'danger',
        condition: (look: LookFullDTO) => look.deleted === true,
        priority: 1,
        order: 1,
        rows: [] as GroupedLook[],
      },
      ...this.categories.map(category => ({
        title: category.translation,
        type: 'default',
        condition: (look: LookFullDTO) => look.category === category.category,
        priority: 0,
        order: 0,
        rows: [] as GroupedLook[],
      })),
    ];
    const byPriority = categories.slice().sort((a, b) => b.priority - a.priority);
    const byOrder = categories.slice().sort((a, b) => a.order - b.order);
    this.filteredLooks.forEach((look) => {
      const condition = byPriority.find(category => category.condition(look));
      if (condition) {
        condition.rows.push(look);
      }
    });
    return byOrder.filter(c => c.rows.length);
    // return byOrder.filter(c => c.rows.length);
    // return this.categories.map(category => ({
    //   title: category.translation,
    //   category: category.category,
    //   rows: this.filteredLooks.filter(look => look.category === category.category),
    // }));
  }

  get groupedThingsByLook(): GroupedLook[] {
    return this.list.map((look) => {
      const groups: Groups = {
        top2: [],
        bottom: [],
        top1: [],
        outerwear: [],
        dress: [],
        shoes: [],
        accessories: [],
        bag: [],
        jewelry: [],
      };
      (look.items || []).forEach((item) => {
        const key = item.aiType as keyof typeof groups | undefined;
        if (!key) return;
        groups[key].push(item);
      });
      return {
        ...look,
        groups,
      };
    });
  }

  @Mutation
  setLoadingList(value: boolean) {
    this.loadingList = value;
  }

  @Mutation
  setLoadingThing(value: boolean) {
    this.loadingThing = value;
  }

  @Mutation
  setSelected(ids: number[]) {
    this.selected = ids;
  }

  @Mutation
  setLooks(list: LookFullDTO[]) {
    this.list = list;
  }

  @Mutation
  setLook(model: LookFullDTO) {
    const index = this.list.findIndex(l => l.id === model.id);
    if (index > -1) {
      // this.list.splice(index, 1, { ...model });
      Vue.set(this.list, index, model);
    } else {
      this.list.push(model);
    }
  }

  @Mutation
  updateLooks(model: LookFullDTO) {
    const index = this.list.findIndex(item => item.id === model.id);
    if (index > -1) {
      Object.assign(this.list[index], model);
    } else {
      this.list.unshift(model);
    }
  }

  @Mutation
  removeLooks(lookIds: number[]) {
    this.list = this.list.filter(look => look.id != null && !lookIds.includes(look.id));
  }

  @Mutation
  addLookThing({ lookId, thing }: {lookId: number; thing: LookItemDTO}) {
    const index = this.list.findIndex(look => look.id === lookId);
    if (index > -1) {
      const look = this.list[index];
      if (look.items) {
        look.items.push(thing);
      } else {
        Vue.set(look, 'items', [thing]);
      }
    }
  }

  @Mutation
  removeLookThing({ lookId, thingId }: {lookId: number, thingId: number}) {
    const index = this.list.findIndex(look => look.id === lookId);
    if (index > -1) {
      const { items } = this.list[index];
      if (items) {
        const i = items.findIndex(item => item.clientItemId === thingId);
        items.splice(i, 1);
      }
    }
  }

  @Mutation
  removeLookCatalogThing({ lookId, thingId }: {lookId: number, thingId: number}) {
    const index = this.list.findIndex(look => look.id === lookId);
    if (index > -1) {
      const { items } = this.list[index];
      if (items) {
        const i = items.findIndex(item => item.catalogItemId === thingId);
        items.splice(i, 1);
      }
    }
  }

  @Mutation
  setCategories(categories: LookCategoryTranslation[]) {
    this.categories = categories;
  }

  @Mutation
  setWeatherFilter(value: WeatherFilterEnum[]) {
    this.weatherFilter = value;
  }

  @Mutation
  setTemp(temp: [number, number]) {
    this.tempFilter = temp;
  }

  @Mutation
  setMulti(multi: boolean) {
    this.multi = multi;
  }

  @Action({ rawError: true })
  async fetchLooks({ userId, status }: {userId: number, status: 'REAL' | 'VIRTUAL' | 'SANDBOX'}) {
    try {
      this.setLoadingList(true);
      this.setLooks([]);
      this.setSelected([]);
      const { data } = await service.looksApi().lookEndpointGetLooks(userId, status);
      amplitude.getInstance().logEvent('Загрузка образов', { clientId: userId, status });
      this.setLooks(data);
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки образов')(e);
    } finally {
      this.setLoadingList(false);
    }
  }

  @Action({ rawError: true, commit: 'updateLooks' })
  async saveLook({ look, userId, status }: { look: Look; userId: number; status: 'REAL' | 'VIRTUAL' | 'SANDBOX'; }): Promise<LookFullDTO> {
    try {
      const {
        category, id: lookId, temperatureFrom, temperatureTo,
      } = look;
      const { data: id } = await service.looksApi()
        .lookEndpointSaveLook(userId, category, lookId, status, temperatureFrom, temperatureTo);

      const model = {
        id,
        category,
        createdAt: look.createdAt || new Date().valueOf(),
      };

      this.setLook(look);

      amplitude.getInstance().logEvent('Сохранение образа', {
        clientId: userId, category, lookId, status,
      });

      return model;
    } catch (e) {
      return throwError('Ошибка сохранения нового образа')(e);
    }
  }

  @Action({ rawError: true, commit: 'updateLooks' })
  async copyLook(look: LookFullDTO) {
    try {
      if (look.id != null) {
        const { data: id } = await service.looksApi().lookEndpointCloneLook(look.id);
        amplitude.getInstance().logEvent('Копирование образа', look.id);
        return { ...look, id, items: (look.items || []).slice() };
      }
      return look;
    } catch (e) {
      return throwError('Ошибка копирования образа')(e);
    }
  }

  @Action({ rawError: true })
  async deleteLook(lookIds: number[]) {
    try {
      await service.looksApi().lookEndpointDeleteLooks(lookIds);
      amplitude.getInstance().logEvent('Удаление образов', lookIds);
      this.removeLooks(lookIds);
      return lookIds;
    } catch (e) {
      return throwError('Ошибка удаления образа')(e);
    }
  }

  @Action({ rawError: true })
  async saveLookThing({ lookId, thing }: {lookId: number; thing: ItemDTO}) {
    try {
      this.setLoadingThing(true);
      if (thing.id != null) {
        await service.looksApi().lookEndpointAddLookItems(lookId, [thing.id]);
        this.addLookThing({ lookId, thing: fromThingToLookItem(thing) });
        amplitude.getInstance().logEvent('Добавление вещи клиента в образ', { lookId, thing });
        return { lookId, thing };
      }
      throw new Error('Нет id');
    } catch (e) {
      return throwError('Ошибка сохранения вещи в лук')(e);
    } finally {
      this.setLoadingThing(false);
    }
  }

  @Action({ rawError: true })
  async saveLookCatalogThing({ lookId, thing }: {lookId: number; thing: CatalogItemShortDTO}) {
    try {
      this.setLoadingThing(true);
      if (thing.id != null) {
        await service.looksApi().lookEndpointAddLookItems(lookId, undefined, [thing.id]);
        this.addLookThing({ lookId, thing: fromCatalogThingToLookItem(thing) });
        amplitude.getInstance().logEvent('Добавление вещи из каталога в образ', { lookId, thing });
        return { lookId, thing };
      }
      throw new Error('Нет id');
    } catch (e) {
      return throwError('Ошибка сохранения вещи в лук')(e);
    } finally {
      this.setLoadingThing(false);
    }
  }

  @Action({ rawError: true })
  async deleteLookThing({ lookId, thingId }: {lookId: number; thingId: number}) {
    try {
      this.setLoadingThing(true);
      await service.looksApi().lookEndpointRemoveLookItems(lookId, [thingId]);
      this.removeLookThing({ lookId, thingId });
      amplitude.getInstance().logEvent('Удаление вещи клиента из образа', { lookId, thingId });
      return { lookId, thingId };
    } catch (e) {
      return throwError('Ошибка удаления вещи из лук')(e);
    } finally {
      this.setLoadingThing(false);
    }
  }

  @Action({ rawError: true })
  async deleteLookCatalogThing({ lookId, thingId }: {lookId: number; thingId: number}) {
    try {
      await service.looksApi().lookEndpointRemoveLookItems(lookId, undefined, [thingId]);
      this.removeLookCatalogThing({ lookId, thingId });
      amplitude.getInstance().logEvent('Удаление вещи из каталога из образа', { lookId, thingId });
      return { lookId, thingId };
    } catch (e) {
      return throwError('Ошибка удаления вещи из лук')(e);
    }
  }

  @Action({ rawError: true })
  async fetchCategories(userId: number) {
    try {
      const { data } = await service.clientsService().clientEndpointGetLookCategories(userId);
      this.setCategories(data);
      amplitude.getInstance().logEvent('Загрузка категорий образов клиента', userId);
      return data;
    } catch (e) {
      return throwError('Ошибка запроса категорий')(e);
    }
  }

  @Action({ rawError: true })
  async saveCategory({ clientId, categoryName }: {clientId: number, categoryName: string}) {
    try {
      const { data } = await service.clientsService().clientEndpointCreateCustomLookCategory(clientId, categoryName);
      const list = this.categories.concat({ category: data, translation: categoryName });
      this.setCategories(list);
      amplitude.getInstance().logEvent('Сохранение категории образов клиента', { clientId, categoryName });
      return list;
    } catch (e) {
      return throwError('Ошибка создания категории')(e);
    }
  }

  @Action({ rawError: true })
  async deleteCategory({ clientId, categoryId }: { clientId: number; categoryId: number }) {
    try {
      await service.clientsService().clientEndpointRemoveCustomLookCategory(clientId, categoryId);
      const index = this.categories.findIndex(item => item.category === categoryId);
      const list = [...this.categories.slice(0, index), ...this.categories.slice(index + 1)];
      this.setCategories(list);
      amplitude.getInstance().logEvent('Удаление категории образов клиента', { clientId, categoryId });
      return list;
    } catch (e) {
      return throwError('Ошибка удаления категории')(e);
    }
  }
}
